import { useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api';
import type { WorkspaceResponse } from '../api';
import { Callout, Input } from '../components/primitives';
import { ErrorNotice, Field, PasswordInput, StatusBadge } from '../components/ui';
import { AccessTokensSection } from '../components/AccessTokens';

export function AccountPage({ workspace }: { workspace: WorkspaceResponse }) {
  const [userName, setUserName] = useState(workspace.identity.label ?? '');

  return (
    <section className="stack">
      <div className="split-panel">
        <div className="stack">
          <ProfileCard
            initialName={userName}
            email={workspace.identity.email ?? ''}
            onUpdated={(newName) => setUserName(newName)}
          />
          <EmailVerificationCard
            email={workspace.identity.email ?? ''}
            verified={workspace.identity.email_verified ?? false}
          />
        </div>
        <DirectChangePasswordCard email={workspace.identity.email ?? ''} />
      </div>
      <AccessTokensSection
        isOwner={workspace.identity.role?.is_owner_role ?? false}
        capabilities={workspace.identity.capabilities ?? []}
      />
    </section>
  );
}

function ProfileCard({
  initialName,
  email,
  onUpdated,
}: {
  initialName: string;
  email: string;
  onUpdated: (newName: string) => void;
}) {
  const [name, setName] = useState(initialName);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    setSaved(false);
    try {
      await api.updateProfile({ name: name.trim() });
      onUpdated(name.trim());
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card">
      <h2>Profile details</h2>
      {saved ? <Callout tone="success">Profile updated.</Callout> : null}
      <form onSubmit={handleSubmit} className="form">
        <Field label="Display name">
          <Input
            required
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
            }}
          />
        </Field>
        <Field label="Email address">
          <Input type="email" disabled value={email} />
        </Field>
        <ErrorNotice error={error} />
        <button
          className="button button--primary"
          type="submit"
          disabled={submitting || !name.trim() || name.trim() === initialName}
        >
          {submitting ? 'Saving' : 'Save profile'}
        </button>
      </form>
    </div>
  );
}

function DirectChangePasswordCard({ email }: { email: string }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fallback reset link state
  const [showForgotFallback, setShowForgotFallback] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<Error | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(new Error('New passwords do not match.'));
      return;
    }
    if (newPassword.length < 10) {
      setError(new Error('New password must be at least 10 characters.'));
      return;
    }
    setSubmitting(true);
    setError(null);
    setSaved(false);
    try {
      await api.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSendResetEmail() {
    setResetSubmitting(true);
    setResetError(null);
    try {
      await api.requestPasswordReset(email);
      setResetSent(true);
    } catch (cause) {
      setResetError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setResetSubmitting(false);
    }
  }

  return (
    <div className="card">
      <h2>Change password</h2>

      {saved ? <Callout tone="success">Password updated.</Callout> : null}

      <form noValidate onSubmit={handleSubmit} className="form">
        <Field label="Current password">
          <PasswordInput
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              setSaved(false);
              setError(null);
            }}
          />
        </Field>
        <Field label="New password" hint="At least 10 characters.">
          <PasswordInput
            required
            minLength={10}
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setSaved(false);
              setError(null);
            }}
          />
        </Field>
        <Field label="Confirm new password">
          <PasswordInput
            required
            minLength={10}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setSaved(false);
              setError(null);
            }}
          />
        </Field>

        <ErrorNotice error={error} />

        <button
          className="button"
          type="submit"
          disabled={submitting || !currentPassword || !newPassword || !confirmPassword}
        >
          {submitting ? 'Updating' : 'Update password'}
        </button>

        <div className="form__aside">
          {!showForgotFallback ? (
            <button
              type="button"
              className="link-button"
              onClick={() => setShowForgotFallback(true)}
            >
              Forgot your current password?
            </button>
          ) : resetSent ? (
            <Callout tone="success">A password reset link was emailed to {email}.</Callout>
          ) : (
            <div>
              <ErrorNotice error={resetError} />
              <button
                className="button button--secondary"
                type="button"
                onClick={handleSendResetEmail}
                disabled={resetSubmitting}
              >
                {resetSubmitting ? 'Sending link' : 'Email me a reset link'}
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

function EmailVerificationCard({ email, verified }: { email: string; verified: boolean }) {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function handleSend() {
    setSubmitting(true);
    setError(null);
    try {
      await api.requestEmailVerification();
      setSent(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  const isEmailNotConfigured = error?.message?.includes('email delivery is not configured') || error?.message?.includes('email_not_configured');

  return (
    <div className="card">
      <h2>Email verification</h2>
      <p className="summary-line">
        <span>{email}</span>
        <StatusBadge kind="emailVerification" value={verified ? 'verified' : 'not_verified'} />
      </p>

      {!verified ? (
        sent ? (
          <Callout tone="success">Check your inbox for a verification link.</Callout>
        ) : (
          <>
            <button className="button button--secondary" type="button" onClick={handleSend} disabled={submitting}>
              {submitting ? 'Sending' : 'Send verification email'}
            </button>
            {isEmailNotConfigured ? (
              <Callout tone="info">Email delivery is not set up for this installation. Ask an administrator to configure SMTP.</Callout>
            ) : (
              <ErrorNotice error={error} />
            )}
          </>
        )
      ) : null}
    </div>
  );
}
